import type { Constructor, Container } from '@packages/core/container';

import { StsBackendClient } from '@packages/clients';
import { HttpClient } from '@packages/core/http';

import type { SubmitAnswersBody, SubmitAnswersResult } from './types';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { toWorksheetSubmissionEntityFromPatchResult } from '../helpers/mappers';

/** Parameters for submitting worksheet answers. */
export type SubmitAnswersParams = {
    /** The worksheet answer payload including answers, status, and case identifiers. */
    body: SubmitAnswersBody;
};

/**
 * Submits worksheet answers (draft or final) for a given worksheet submission.
 * Refreshes the auth token before submission to prevent token expiry during long form sessions.
 */
export class SubmitAnswers extends StsBackendOperation {
    /**
     * Register this operation with both HttpClient and StsBackendClient dependencies.
     *
     * @param container - the container to register in
     */
    static override registerInContainer(container: Container): void {
        container.register(SubmitAnswers as unknown as Constructor, [HttpClient, StsBackendClient]);
    }

    /** The HTTP client used to refresh the auth token before submission. */
    private httpClient: HttpClient;

    /**
     * @param httpClient - the HTTP client used to refresh the auth token
     * @param client - the backend API client
     */
    constructor(httpClient: HttpClient, client: StsBackendClient) {
        super(client);
        this.httpClient = httpClient;
    }

    /**
     * Refresh the auth token and submit worksheet answers to the API.
     *
     * @param params - the worksheet answer payload
     * @throws {Error} when the API response indicates failure
     * @returns the submission entity and a map of element IDs to server-assigned source IDs
     */
    async exec(params: SubmitAnswersParams): Promise<SubmitAnswersResult> {
        const { body } = params;

        // Refresh the auth token to prevent expiry during long form sessions
        await this.httpClient.refreshToken();

        // Submit the answers to the API
        const response = await this.client.worksheetAnswerSubmission(body);

        if (!response.success) {
            throw new Error(`Worksheet answer submission failed: ${response.data?.message ?? 'Unknown error'}`);
        }

        // Map the API result to a domain entity
        const entity = toWorksheetSubmissionEntityFromPatchResult(response.data.results);

        // Extract server-assigned sourceIds from the response answers
        const answerSourceIds: Record<string, string> = {};
        for (const answer of response.data.results.answers) {
            if (answer.sourceId) {
                answerSourceIds[answer.elementId] = answer.sourceId;
            }
        }

        return { answerSourceIds, entity };
    }
}
